import React from "react";
import { StatusBadge } from "@/components/StatusBadge";
import { ImageGallery } from "@/components/ImageGallery";
import { fileUrl } from "@/lib/api";
import { FileText } from "lucide-react";
import {
  URGENCY_LABELS,
  URGENCY_STYLES,
  ROLE_LABELS,
  fmtDate,
  fmtDateShort,
  fmtRupiah,
} from "@/lib/constants";

const Field = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 border-b border-dashed border-slate-200 py-2 last:border-0">
    <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
      {label}
    </span>
    <span className="text-right text-sm font-medium text-slate-800">{value}</span>
  </div>
);

const SectionTitle = ({ children }) => (
  <h2 className="mb-3 border-b-2 border-slate-800 pb-1 font-display text-[13px] font-extrabold uppercase tracking-[0.14em] text-slate-800">
    {children}
  </h2>
);

const BapsAttachment = ({ id, contentType }) => {
  const isImage = !contentType || contentType.startsWith("image");
  const [imgOk, setImgOk] = React.useState(isImage);
  return (
    <a
      href={fileUrl(id)}
      target="_blank"
      rel="noreferrer"
      data-testid="baps-attachment"
      className="inline-flex items-center gap-3 rounded-md border border-slate-200 p-3 hover:bg-slate-50"
    >
      {imgOk ? (
        <img
          src={fileUrl(id)}
          onError={() => setImgOk(false)}
          alt="Lampiran BAPS"
          className="h-20 w-20 rounded border border-slate-200 object-cover"
        />
      ) : (
        <span className="flex h-20 w-20 items-center justify-center rounded bg-slate-100">
          <FileText className="h-8 w-8 text-slate-500" />
        </span>
      )}
      <span className="text-sm font-medium text-teal-700 underline">
        Buka Dokumen BAPS Bertanda Tangan
      </span>
    </a>
  );
};

export function ReportDocument({ r }) {
  const beforeFiles = r.damage_files || [];
  const afterFiles = (r.repair_files || []).map((f) => (typeof f === "string" ? f : f.file_id));

  return (
    <article
      data-testid="report-document"
      className="mx-auto w-full max-w-[900px] rounded-md border border-slate-200 bg-white p-6 shadow-sm sm:p-10"
    >
      {/* Header */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">
            Dokumen Resmi{r.division_name ? ` · Divisi ${r.division_name}` : ""}
          </p>
          <h1 className="mt-2 font-display text-2xl font-extrabold uppercase leading-tight tracking-tight text-slate-900 sm:text-3xl">
            Laporan Perbaikan
            <br />
            Kendaraan Dinas
          </h1>
        </div>
        <div className="text-right">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
            Nomor Laporan
          </p>
          <p className="font-num text-xl font-extrabold text-slate-900">{r.report_number}</p>
          <div className="mt-2 flex justify-end">
            <StatusBadge status={r.status} />
          </div>
        </div>
      </header>

      {/* Data kendaraan & pelapor */}
      <section className="mt-8">
        <SectionTitle>Data Kendaraan &amp; Pelapor</SectionTitle>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
            <Field label="Nomor Polisi" value={<span className="font-num font-bold">{r.plate_number}</span>} />
            <Field
              label="Kendaraan"
              value={`${r.brand || "-"} ${r.vehicle_type || ""}${r.year ? ` (${r.year})` : ""}`}
            />
            <Field label="Tgl Kejadian" value={fmtDateShort(r.incident_date)} />
            <Field label="Lokasi" value={r.location || "-"} />
          </div>
          <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
            <Field label="Pelapor" value={r.reporter_name || "-"} />
            <Field label="Divisi" value={r.division_name || "-"} />
            <Field label="No. Telepon" value={r.phone || "-"} />
            <Field
              label="Urgensi"
              value={
                <span
                  className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${URGENCY_STYLES[r.urgency]}`}
                >
                  {URGENCY_LABELS[r.urgency]}
                </span>
              }
            />
          </div>
        </div>
      </section>

      {/* Deskripsi */}
      <section className="mt-8">
        <SectionTitle>Deskripsi Kerusakan</SectionTitle>
        <p className="border-l-2 border-[var(--sipkd-primary)] bg-slate-50 px-4 py-3 text-sm text-slate-700">
          {r.damage_description}
        </p>
        {r.status === "ditolak" && r.reject_reason && (
          <p className="mt-3 border-l-2 border-rose-400 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            <strong>Alasan penolakan:</strong> {r.reject_reason}
          </p>
        )}
      </section>

      {/* Kronologi */}
      <section className="mt-8">
        <SectionTitle>Kronologi</SectionTitle>
        <div className="space-y-1.5">
          {(r.timeline || []).map((e, i) => (
            <div
              key={i}
              data-testid={`timeline-item-${i}`}
              className="grid grid-cols-[14px_120px_170px_1fr] items-center gap-3 border-b border-dashed border-slate-200 py-1.5 text-xs last:border-0 sm:grid-cols-[14px_130px_180px_1fr]"
            >
              <span
                className={`h-2.5 w-2.5 rounded-sm border ${
                  i === (r.timeline || []).length - 1
                    ? "border-[var(--sipkd-primary)] bg-[var(--sipkd-primary)]"
                    : "border-slate-300 bg-white"
                }`}
              />
              <span className="font-num text-slate-500">{fmtDate(e.at)}</span>
              <StatusBadge status={e.status} className="justify-self-start scale-[0.92]" />
              <span className="truncate text-slate-600">
                <span className="font-medium text-slate-800">{e.by_name}</span> — {e.note}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Sparepart */}
      <section className="mt-8">
        <SectionTitle>Daftar Item / Sparepart Diganti</SectionTitle>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-left text-sm" data-testid="doc-parts-table">
            <thead className="bg-slate-800 text-[10px] uppercase tracking-[0.12em] text-white">
              <tr>
                <th className="w-14 px-3 py-2 font-semibold">No</th>
                <th className="px-3 py-2 font-semibold">Nama Item</th>
                <th className="w-24 px-3 py-2 font-semibold">Jumlah</th>
                <th className="w-1/3 px-3 py-2 font-semibold">Keterangan</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {(r.spare_parts || []).length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-4 text-center text-xs text-slate-400">
                    Tidak ada item diganti.
                  </td>
                </tr>
              ) : (
                r.spare_parts.map((p, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2 font-num text-slate-500">{i + 1}</td>
                    <td className="px-3 py-2 text-slate-800">{p.name}</td>
                    <td className="px-3 py-2 font-num text-slate-700">{p.qty}</td>
                    <td className="px-3 py-2 text-slate-500">{p.note || "-"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {r.repair_cost > 0 && (
          <div
            className="mt-3 flex items-center justify-between border-t-2 border-slate-800 pt-2 text-sm"
            data-testid="report-cost"
          >
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
              Total Biaya Perbaikan
            </span>
            <span className="font-num text-lg font-extrabold text-slate-900">
              {fmtRupiah(r.repair_cost)}
            </span>
          </div>
        )}
      </section>

      {/* BAPS */}
      {r.baps_number && (
        <section className="mt-8" data-testid="doc-baps-section">
          <SectionTitle>Berita Acara Penggantian Sparepart (BAPS)</SectionTitle>
          <div className="grid gap-x-8 gap-y-1 sm:grid-cols-2">
            <Field label="Nomor BAPS" value={<span className="font-num">{r.baps_number}</span>} />
            <Field label="Tanggal" value={r.baps_created_at ? fmtDateShort(r.baps_created_at) : "-"} />
            <Field
              label="Status"
              value={r.baps_status === "ditandatangani" ? "Ditandatangani" : "Menunggu tanda tangan basah"}
            />
            <Field label="Alasan" value={r.baps_reason || "-"} />
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[520px] text-left text-sm" data-testid="doc-baps-table">
              <thead className="bg-slate-800 text-[10px] uppercase tracking-[0.12em] text-white">
                <tr>
                  <th className="w-14 px-3 py-2 font-semibold">No</th>
                  <th className="px-3 py-2 font-semibold">Nama Sparepart</th>
                  <th className="w-20 px-3 py-2 font-semibold">Jumlah</th>
                  <th className="px-3 py-2 text-right font-semibold">Harga Satuan</th>
                  <th className="px-3 py-2 text-right font-semibold">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {(r.baps_spare_parts || []).map((p, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2 font-num text-slate-500">{i + 1}</td>
                    <td className="px-3 py-2 text-slate-800">{p.name}</td>
                    <td className="px-3 py-2 font-num text-slate-700">{p.qty}</td>
                    <td className="px-3 py-2 text-right font-num text-slate-700">{fmtRupiah(p.unit_price)}</td>
                    <td className="px-3 py-2 text-right font-num text-slate-800">{fmtRupiah(p.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {r.baps_signed_file_id && (
            <div className="mt-4">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                Lampiran — BAPS bertanda tangan basah
              </p>
              <BapsAttachment id={r.baps_signed_file_id} contentType={r.baps_signed_content_type} />
            </div>
          )}
        </section>
      )}
      <section className="mt-8">
        <SectionTitle>Dokumentasi</SectionTitle>
        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <ImageGallery ids={beforeFiles} label="Sebelum perbaikan" />
            {beforeFiles.length === 0 && (
              <p className="rounded-md border border-dashed border-slate-300 px-4 py-8 text-center text-xs text-slate-400">
                Foto kondisi sebelum perbaikan belum tersedia
              </p>
            )}
          </div>
          <div>
            <ImageGallery ids={afterFiles} label="Sesudah perbaikan" />
            {afterFiles.length === 0 && (
              <p className="rounded-md border border-dashed border-slate-300 px-4 py-8 text-center text-xs text-slate-400">
                Foto kondisi sesudah perbaikan belum tersedia
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Tanda tangan */}
      <section className="mt-8 grid gap-4 sm:grid-cols-2">
        {[
          { title: "Disetujui oleh", name: r.approver_name, date: r.approver_date },
          {
            title: "Teknisi Bengkel",
            name: r.technician_name || r.followup_pic_name,
            date: r.technician_date,
            note: r.followup_pic_role ? ROLE_LABELS[r.followup_pic_role] : null,
          },
        ].map((s) => (
          <div key={s.title} className="rounded-md border border-slate-200 p-4">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
              {s.title}
            </p>
            <p className="mt-8 font-display text-base font-bold text-slate-900">{s.name || "-"}</p>
            <p className="text-xs text-slate-400">
              {s.date ? fmtDateShort(s.date) : ""} {s.note ? `• ${s.note}` : ""}
            </p>
          </div>
        ))}
      </section>

      <footer className="mt-8 flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 pt-3 text-[10px] uppercase tracking-[0.14em] text-slate-400">
        <span className="font-num">{r.report_number}</span>
        <span>Laporan Perbaikan Kendaraan Dinas</span>
        <span>Halaman 1 dari 1</span>
      </footer>
    </article>
  );
}
